import { useTranslation } from "react-i18next";
import { cn, Tooltip, TooltipContent, TooltipTrigger } from "@nexus/ui";
import type { ExploreApplyDisableReason } from "../../../lib/can-apply-explore-filters";

interface ExploreApplyControlsProps {
  isDirty: boolean;
  canApply: boolean;
  hasPendingPreset?: boolean;
  disableReason?: ExploreApplyDisableReason;
  onApply: () => void;
  onDiscard: () => void;
}

const DISABLE_REASON_KEYS: Record<ExploreApplyDisableReason, [string, string]> = {
  incomplete: [
    "dashboard.exploreApplyControls.completeRequiredFilters",
    "Complete the required filters to apply",
  ],
  "roster-loading": [
    "dashboard.exploreApplyControls.waitForCompetitorOptions",
    "Wait for competitors to load",
  ],
  "roster-error": [
    "dashboard.globalFilterHeader.mainCompetitorError",
    "Failed to load competitors",
  ],
};

/** Apply/Discard guardrail for explore filter headers — draft edits apply only on click. */
export function ExploreApplyControls({
  isDirty,
  canApply,
  hasPendingPreset = false,
  disableReason,
  onApply,
  onDiscard,
}: ExploreApplyControlsProps) {
  const { t } = useTranslation();
  const isActionable = (isDirty && canApply) || hasPendingPreset;
  const showApplyLabel = isDirty || hasPendingPreset;
  const tooltipKey = DISABLE_REASON_KEYS[disableReason ?? "incomplete"];

  return (
    <div className="flex shrink-0 items-center gap-2">
      {isDirty && (
        <button
          type="button"
          onClick={onDiscard}
          className="inline-flex h-8 items-center rounded-lg px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          {t("dashboard.exploreApplyControls.discard", "Discard")}
        </button>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <button
              type="button"
              onClick={onApply}
              disabled={!isActionable}
              className={cn(
                "inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold transition-colors",
                isActionable
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "cursor-not-allowed bg-muted text-muted-foreground opacity-60",
              )}
            >
              {isDirty && (
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" aria-hidden />
              )}
              {showApplyLabel
                ? t("dashboard.exploreApplyControls.applyChanges", "Apply changes")
                : t("dashboard.exploreApplyControls.applied", "Applied")}
            </button>
          </div>
        </TooltipTrigger>
        {isDirty && !canApply && (
          <TooltipContent>
            {t(tooltipKey[0], tooltipKey[1])}
          </TooltipContent>
        )}
      </Tooltip>
    </div>
  );
}
